import { useEffect, useMemo, useState } from 'react';
import { FiCheck, FiEye, FiFileText, FiRefreshCw, FiSave, FiSearch } from 'react-icons/fi';
import { API_CORE } from '../../../../../_CORE/API/ApiCORE';
import { FieldSelectSearch } from '../../../../../_CORE/APP/Templates/_UI/Form/_index';
import { notify } from '../../../../../_CORE/APP/Templates/_UI/Alerts/AppAlertHost';
import { usePortfolioAdmin } from '../../_Shared/_Hooks/usePortfolioAdmin';
import { SALE_PROJECTS_ADMIN_API as API } from '../_MODULES/_API';

const STATUS_OPTIONS = [
    ['pending_review', 'Pendiente de revisión'],
    ['approved', 'Pago aprobado'],
    ['rejected', 'Rechazado'],
    ['demo_sent', 'Demo / entrega enviada'],
    ['deployment_coordination', 'Coordinando deploy'],
    ['completed', 'Completado'],
];

const STATUS_LABELS = Object.fromEntries(STATUS_OPTIONS);
const STATUS_SELECT_OPTIONS = STATUS_OPTIONS.map(([value, label]) => ({ value, label }));
const STATUS_FILTER_OPTIONS = [{ value: '', label: 'Todos los estados' }, ...STATUS_SELECT_OPTIONS];
const PER_PAGE = 8;

const formatPrice = (price, currency = 'ARS') => {
    try { return new Intl.NumberFormat('es-AR', { style: 'currency', currency, maximumFractionDigits: 0 }).format(Number(price || 0)); }
    catch { return `${currency} ${price}`; }
};

const dateLabel = (value) => {
    const date = value ? new Date(String(value).replace(' ', 'T')) : null;
    if (!date || Number.isNaN(date.getTime())) return value || '-';
    return new Intl.DateTimeFormat('es-AR', { dateStyle: 'short', timeStyle: 'short' }).format(date);
};

export const SaleOrdersPanel = () => {
    const api = usePortfolioAdmin();
    const [items, setItems] = useState([]);
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState('');
    const [page, setPage] = useState(1);
    const [drafts, setDrafts] = useState({});

    const load = async () => {
        try {
            const data = await api.get(API.ORDERS);
            const rows = Array.isArray(data) ? data : [];
            setItems(rows);
            setDrafts(Object.fromEntries(rows.map((item) => [item.id, { status: item.status || 'pending_review', admin_note: item.admin_note || '' }])));
        } catch (error) {
            notify.error('No se pudieron cargar las compras', error?.message || 'Reintenta en unos segundos.');
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        return items.filter((item) => {
            if (status && item.status !== status) return false;
            if (!term) return true;
            return [item.order_code, item.project_title, item.buyer_name, item.buyer_email, item.buyer_contact]
                .some((value) => String(value || '').toLowerCase().includes(term));
        });
    }, [items, search, status]);

    useEffect(() => setPage(1), [search, status]);
    const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
    const safePage = Math.min(page, totalPages);
    const visible = filtered.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE);

    const updateDraft = (id, field, value) => setDrafts((current) => ({
        ...current,
        [id]: { ...(current[id] || {}), [field]: value },
    }));

    const save = async (item) => {
        const draft = drafts[item.id] || { status: item.status, admin_note: item.admin_note || '' };
        try {
            await api.securePost(API.ORDER_STATUS, { id: item.id, ...draft }, {}, {
                successTitle: 'Compra actualizada',
                successText: `El pedido ${item.order_code} ahora figura como ${STATUS_LABELS[draft.status] || draft.status}.`,
            });
            await load();
        } catch (_) {}
    };

    const openReceipt = async (item) => {
        const popup = window.open('', '_blank');
        try {
            const endpoint = API_CORE.ENDPOINTS[API.ORDER_RECEIPT];
            const blob = await API_CORE.SERVICES.GET_RESOURCE(`${endpoint}?id=${encodeURIComponent(item.id)}`, { responseType: 'blob' });
            if (!(blob instanceof Blob)) throw new Error('No se pudo abrir el comprobante.');
            const url = URL.createObjectURL(blob);
            if (popup) popup.location.href = url;
            else window.open(url, '_blank', 'noopener,noreferrer');
            window.setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (error) {
            if (popup) popup.close();
            notify.error('Comprobante no disponible', error?.message || 'No se pudo abrir el archivo.');
        }
    };

    return (
        <section className="PBADMIN__panel PBADMIN__sales_orders">
            <div className="PBADMIN__sales_orders_head">
                <div><span>VENTAS</span><h2>Solicitudes de compra</h2><p>Revisá comprobantes, aprobá pagos y llevá el seguimiento de cada entrega.</p></div>
                <button type="button" onClick={load}><FiRefreshCw /> Actualizar</button>
            </div>

            <div className="PBADMIN__sales_orders_tools">
                <label><FiSearch /><input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por código, proyecto o comprador…" /></label>
                <div className="PBADMIN__sales_status_filter">
                    <FieldSelectSearch
                        id="sales_status_filter"
                        label={null}
                        options={STATUS_FILTER_OPTIONS}
                        value={STATUS_FILTER_OPTIONS.find((option) => option.value === status) || STATUS_FILTER_OPTIONS[0]}
                        onChange={(option) => setStatus(option?.value || '')}
                        isClearable={false}
                        isSearchable={false}
                    />
                </div>
                <span>{filtered.length} resultado{filtered.length === 1 ? '' : 's'}</span>
            </div>

            <div className="PBADMIN__sales_orders_list">
                {visible.map((item) => {
                    const draft = drafts[item.id] || {};
                    return (
                        <article key={item.id} className="PBADMIN__sale_order_card">
                            <div className="PBADMIN__sale_order_top">
                                <div><span className={`PBADMIN__sale_status is-${item.status}`}>{STATUS_LABELS[item.status] || item.status}</span><strong>{item.project_title}</strong><small>{item.order_code} · {dateLabel(item.created_at)}</small></div>
                                <div className="PBADMIN__sale_order_amount"><small>Importe</small><strong>{formatPrice(item.project_price, item.currency)}</strong></div>
                            </div>

                            <div className="PBADMIN__sale_order_grid">
                                <div><span>Comprador</span><strong>{item.buyer_name}</strong><small>{item.buyer_email || item.buyer_contact || '-'}</small></div>
                                <div><span>Contacto</span><strong>{item.buyer_contact || '-'}</strong><small>{item.buyer_email || '-'}</small></div>
                                <div><span>Entrega</span><strong>{item.delivery_option === 'deployment' ? 'Ayuda con deploy' : item.delivery_option === 'unsure' ? 'A coordinar' : 'Entrega directa'}</strong><small>{item.buyer_note || 'Sin comentario adicional'}</small></div>
                                <div><span>Comprobante</span><strong>{item.receipt_original_name}</strong><button type="button" onClick={() => openReceipt(item)}><FiEye /> Ver comprobante</button></div>
                            </div>

                            <div className="PBADMIN__sale_order_actions">
                                <div className="PBADMIN__sale_status_editor"><span>Estado</span><FieldSelectSearch id={`sale_status_${item.id}`} label={null} options={STATUS_SELECT_OPTIONS} value={STATUS_SELECT_OPTIONS.find((option) => option.value === (draft.status || item.status)) || null} onChange={(option) => updateDraft(item.id, 'status', option?.value || item.status)} isClearable={false} isSearchable={false} /></div>
                                <label className="is-note"><span>Nota interna</span><input value={draft.admin_note ?? item.admin_note ?? ''} onChange={(e) => updateDraft(item.id, 'admin_note', e.target.value)} placeholder="Ej. Pago verificado, coordinar el viernes…" /></label>
                                <button type="button" className="PBADMIN__primary" onClick={() => save(item)} disabled={api.isLoading}><FiSave /> Guardar</button>
                            </div>
                        </article>
                    );
                })}

                {visible.length === 0 && <div className="PBADMIN__sales_orders_empty"><FiFileText /><strong>No hay solicitudes con estos filtros.</strong></div>}
            </div>

            {filtered.length > PER_PAGE && (
                <div className="PBADMIN__sales_orders_pagination">
                    <button type="button" disabled={safePage <= 1} onClick={() => setPage((current) => Math.max(1, current - 1))}>Anterior</button>
                    <span>Página <strong>{safePage}</strong> de {totalPages}</span>
                    <button type="button" disabled={safePage >= totalPages} onClick={() => setPage((current) => Math.min(totalPages, current + 1))}>Siguiente</button>
                </div>
            )}
        </section>
    );
};
